/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import {
  Body,
  Button,
  Field,
  FieldError,
  FieldLabel,
  Sheet,
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Skeleton,
} from '@e2n/ui/v2';
import { Beruf } from '@/domain/mitarbeiter/features/rotationsplan/entities/Beruf';
import { Mitarbeiter } from '@/domain/mitarbeiter/features/rotationsplan/entities/Mitarbeiter';
import { MitarbeiterSelect } from '@/domain/mitarbeiter/features/rotationsplan/features/CreateRotationsplan/components/MitarbeiterSelect';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import { toast } from '@/hooks/use-toast';
import { useForm } from '@tanstack/react-form';
import { Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import z from 'zod';
import * as Sentry from '@sentry/react';
import { BerufeSelect } from '@/domain/mitarbeiter/features/rotationsplan/features/CreateRotationsplan/components/BerufeSelectV2';
import { useCreateRotationsplan } from '@/domain/mitarbeiter/features/rotationsplan/hooks/useCreateRotationsplan/useCreateRotationsplan';
import { checkIfMitarbeiterHasRotationsplan } from '@/domain/mitarbeiter/features/rotationsplan/features/CreateRotationsplan/checkIfMitarbeiterHasRotationsplan';
import { useRotationsplaene } from '@/domain/mitarbeiter/features/rotationsplan/hooks/useRotationsplaene/useRotationsplaene';
import { ConfirmDialog } from '@/domain/mitarbeiter/features/rotationsplan/features/CreateRotationsplan/ConfirmDialog';

export function CreateRotationsplan() {
  const { t } = useTranslation();
  const [createRotationsplan] = useCreateRotationsplan();
  const { currentStandort, standorte } = useStandortContext();
  const navigate = useNavigate();
  const standortIds = standorte.map((standort) => standort.id);
  const { rotationsplaene } = useRotationsplaene(standortIds);
  const [rotationsplanExistiertBereits, setRotationsplanExistiertBereits] =
    useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const schema = z.object({
    beruf: z.instanceof(Beruf, {
      message: t('generalValidationError.required'),
    }),
    mitarbeiter: z.instanceof(Mitarbeiter, {
      message: t('generalValidationError.required'),
    }),
  });

  const form = useForm({
    defaultValues: {
      beruf: undefined as unknown as Beruf,
      mitarbeiter: undefined as unknown as Mitarbeiter,
    },
    validators: {
      onChange: schema,
    },
    onSubmit: async ({
      value,
    }: {
      value: { beruf: Beruf; mitarbeiter: Mitarbeiter };
    }) => {
      const showError = () =>
        toast({
          variant: 'destructive',
          title: t(
            'domain.mitarbeiter.rotationsplan.rotationsplaene.createRotationsplan.error',
          ),
        });

      try {
        const result = await createRotationsplan({
          input: {
            berufId: value.beruf.id,
            mitarbeiterId: value.mitarbeiter.id,
            standortId: currentStandort.id,
          },
        });

        if (!result.data?.mitarbeiter_createRotationsplan.id) {
          showError();
          return;
        }

        toast({
          title: t(
            'domain.mitarbeiter.rotationsplan.rotationsplaene.createRotationsplan.success',
          ),
        });
        setRotationsplanExistiertBereits(false);
        setIsOpen(false);
        navigate(
          `/mitarbeiter/rotationsplan/${result.data.mitarbeiter_createRotationsplan.id}`,
        );
      } catch (error) {
        Sentry.captureException(error);
        showError();
      }
    },
  });

  function openConfirmDialog() {
    form.validateAllFields('change');

    if (form.state.isValid) {
      setIsOpen(true);
    }
  }

  return (
    <Sheet
      open
      onOpenChange={(open) => {
        if (!open) {
          form.reset();
          navigate('/mitarbeiter/rotationsplaene/uebersicht', {
            replace: true,
          });
        }
      }}>
      <SheetContent
        aria-describedby={undefined}
        className="data-[side=right]:w-full sm:data-[side=right]:max-w-130">
        <SheetHeader>
          <SheetTitle className="flex-1">
            {t(
              'domain.mitarbeiter.rotationsplan.rotationsplaene.createRotationsplan.titel',
            )}
          </SheetTitle>
          {rotationsplanExistiertBereits ? (
            <Button
              className="md:hidden"
              disabled={form.state.isSubmitting}
              onClick={openConfirmDialog}>
              {t('actions.fertig')}
            </Button>
          ) : (
            <Button
              className="md:hidden"
              disabled={!form.state.isValid || form.state.isSubmitting}
              form="createRotationsplanForm">
              {t('actions.fertig')}
            </Button>
          )}
        </SheetHeader>
        <SheetBody className="flex flex-col gap-4 sm:p-6">
          <Body size="sm" className="text-labels-secondary">
            {t(
              'domain.mitarbeiter.rotationsplan.rotationsplaene.hinweisPflichtfelder',
            )}
          </Body>
          <form
            id="createRotationsplanForm"
            className="flex flex-col gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              form.handleSubmit();
            }}>
            <form.Field
              name="mitarbeiter"
              listeners={{
                onChange: ({ value }) => {
                  if (!value) {
                    setRotationsplanExistiertBereits(false);
                    return;
                  }

                  const rotationsplanExistiert =
                    checkIfMitarbeiterHasRotationsplan(
                      value.id,
                      rotationsplaene,
                    );
                  setRotationsplanExistiertBereits(rotationsplanExistiert);
                },
              }}>
              {(field) => {
                const isInvalid =
                  field.state.meta.isTouched && !field.state.meta.isValid;

                return (
                  <Field data-invalid={isInvalid}>
                    <FieldLabel
                      htmlFor={field.name}
                      invalid={isInvalid}
                      requiredField>
                      {t(
                        'domain.mitarbeiter.rotationsplan.rotationsplaene.createRotationsplan.fields.mitarbeiter.label',
                      )}
                    </FieldLabel>
                    <Suspense fallback={<Skeleton className="w-full h-10" />}>
                      <MitarbeiterSelect
                        id={field.name}
                        invalid={isInvalid}
                        onChange={field.handleChange}
                        value={field.state.value}
                      />
                    </Suspense>
                    {isInvalid && (
                      <FieldError errors={field.state.meta.errors} />
                    )}
                    {rotationsplanExistiertBereits && (
                      <FieldError
                        variant="warning"
                        errors={[
                          {
                            message: t(
                              'domain.mitarbeiter.rotationsplan.rotationsplaene.createRotationsplan.planExistiert.validationMessage',
                            ),
                          },
                        ]}
                      />
                    )}
                  </Field>
                );
              }}
            </form.Field>
            <form.Field name="beruf">
              {(field) => {
                const isInvalid =
                  field.state.meta.isTouched && !field.state.meta.isValid;

                return (
                  <Field data-invalid={isInvalid}>
                    <FieldLabel
                      htmlFor={field.name}
                      invalid={isInvalid}
                      requiredField>
                      {t(
                        'domain.mitarbeiter.rotationsplan.rotationsplaene.createRotationsplan.fields.beruf.label',
                      )}
                    </FieldLabel>
                    <BerufeSelect
                      handleChange={field.handleChange}
                      id={field.name}
                      invalid={isInvalid}
                      value={field.state.value}
                    />
                    {isInvalid && (
                      <FieldError errors={field.state.meta.errors} />
                    )}
                  </Field>
                );
              }}
            </form.Field>
          </form>
        </SheetBody>
        <SheetFooter className="hidden md:flex">
          {rotationsplanExistiertBereits ? (
            <Button
              disabled={form.state.isSubmitting}
              onClick={openConfirmDialog}>
              {t('actions.fertig')}
            </Button>
          ) : (
            <Button
              disabled={!form.state.isValid || form.state.isSubmitting}
              form="createRotationsplanForm">
              {t('actions.fertig')}
            </Button>
          )}
        </SheetFooter>
        {isOpen && rotationsplanExistiertBereits && (
          <ConfirmDialog
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            isSubmitting={form.state.isSubmitting}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}
